"use client";

import Link from "next/link";
import { CartButton } from "@/components/cart/cart-button";
import { AFSLogo } from "../assets/logos";
import Container from "../ui/container";
import { UserButton } from "../auth/user-button";
import { COMPANY } from "@/lib/constants";
import { MobileSidebarTrigger } from "@/components/sidebar/mobile-sidebar-trigger";
import { ShopNav } from "./shop-nav";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { brands } from "@/lib/constants/nav";
import { useState, useEffect } from "react";
import { Session } from "next-auth";
import { SearchBar } from "./search-bar";
import { SearchResult } from "@/store/search-store";
import {
  Contact,
  MapPin,
  ShoppingBag,
  ShoppingCart,
  FileText,
  Car,
  Store,
} from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { usePathname } from "next/navigation";

interface NavPagesProps {
  isAuthenticated?: boolean;
  hidden?: boolean;
  disabled?: boolean;
}

interface MenuItem {
  label: string;
  href?: string;
  auth?: boolean;
  className?: string;
  download?: boolean | string;
}

const navigation = [
  { label: "Contact", href: "/contact" },
  { label: "Find a Distributor", href: "/distributors" },
  {
    label: "Download Catalogue",
    href: "/Rock-Board-Catalogue-2024_2_0.pdf",
    className: "text-primary",
    download: "Rock-Board-Catalogue-2024_2_0.pdf",
  },
  { label: "My FleetHub", href: "/my-fleethub", auth: true },
] as MenuItem[];

interface NavbarProps {
  authProps: {
    session: Session | null;
    LoginButton: React.ReactNode;
    status: "loading" | "authenticated" | "unauthenticated";
  };
  searchAction?: (query: string) => Promise<{
    success?: boolean;
    results?: SearchResult[];
  }>;
}

const NavPages: React.FC<NavPagesProps> = ({
  isAuthenticated,
  hidden,
  disabled,
}) => {
  if (hidden) return null;

  return (
    <nav className='hidden md:flex w-full h-full items-end lg:items-center justify-end '>
      <div className='flex gap-8'>
        <div className='lg:flex hidden items-center'>
          <ShopNav disabled={disabled} />
          {navigation.map((item, index) => {
            if (item.auth && !isAuthenticated) {
              return null;
            }

            if (item.download) {
              return (
                <a
                  key={index}
                  href={
                    disabled ? undefined : `/${item.href}`.replace(/\/+/g, "/")
                  }
                  className={
                    disabled
                      ? "px-4 mx-4 py-2 rounded-md text-sm cursor-not-allowed text-muted-foreground"
                      : `px-4 mx-4 py-2 rounded-md text-sm cursor-pointer hover:bg-primary hover:text-white ${item.className || ""}`
                  }
                  download={disabled ? undefined : item.download || ""}
                  target={disabled ? undefined : "_blank"}
                  rel={disabled ? undefined : "noopener noreferrer"}
                  onClick={disabled ? (e) => e.preventDefault() : undefined}
                >
                  {item.label}
                </a>
              );
            }

            return (
              <Link
                key={index}
                href={disabled ? "#" : item.href || "#"}
                className={
                  disabled
                    ? "px-4 mx-4 py-2 rounded-md text-sm cursor-not-allowed text-muted-foreground"
                    : `px-4 mx-4 py-2 rounded-md text-sm cursor-pointer hover:bg-primary hover:text-white ${item.className || ""}`
                }
                onClick={disabled ? (e) => e.preventDefault() : undefined}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default function Navbar({ authProps, searchAction }: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [shouldShowNav, setShouldShowNav] = useState(true);
  const [isAccessDeniedPage, setIsAccessDeniedPage] = useState(false);
  const pathname = usePathname();

  // Check if we're on the access-denied page to disable cart/search
  useEffect(() => {
    setIsAccessDeniedPage(pathname === "/access-denied");
  }, [pathname]);

  // Function to handle search expansion state changes
  const handleSearchExpandChange = (expanded: boolean) => {
    if (expanded) {
      // Immediately hide nav when search opens
      setShouldShowNav(false);
    } else {
      // Delay showing nav when search closes
      setTimeout(() => {
        setShouldShowNav(true);
      }, 200);
    }
    setIsSearchExpanded(expanded);
  };

  // Function to close all menus
  const closeAllMenus = () => {
    setIsOpen(false);
  };

  // Extract company ID from session for search
  const userCompanyId = authProps.session?.user?.companyId || undefined;

  const renderAuthButton = () => {
    // If session is loading, return a loading indicator or placeholder
    if (authProps.status === "loading") {
      return (
        <Button variant='ghost' size='sm' disabled className='opacity-50'>
          <span className='h-5 w-5 animate-pulse rounded-full bg-muted'></span>
        </Button>
      );
    }

    // If no session, return the login button
    if (!authProps.session) {
      return authProps.LoginButton;
    }

    // Otherwise, return the user button
    return <UserButton />;
  };

  return (
    <header className='text-[#f2f2f2] border-b border-[#3b3b3b] px-4'>
      <Container>
        <nav className='flex items-center justify-between h-16 relative'>
          <div className='flex items-center gap-4 z-[201]'>
            <div className='lg:hidden flex items-center gap-2'>
              {pathname !== "/" && <BackButton disabled={isAccessDeniedPage} />}
              {authProps.session && authProps.session.user?.role !== "user" && (
                <MobileSidebarTrigger disabled={isAccessDeniedPage} />
              )}
            </div>
            <div className='hidden lg:flex items-center'>
              <Link
                href={isAccessDeniedPage ? "#" : "/"}
                aria-label={`${COMPANY.name} Logo`}
                className={
                  isAccessDeniedPage ? "w-auto cursor-not-allowed" : "w-auto"
                }
                onClick={
                  isAccessDeniedPage ? (e) => e.preventDefault() : closeAllMenus
                }
              >
                <AFSLogo
                  width={80}
                  height={33}
                  className={
                    isAccessDeniedPage
                      ? "[&_path.fill-foreground]:fill-white [&_path.fill-primary]:fill-primary opacity-50"
                      : "[&_path.fill-foreground]:fill-white [&_path.fill-primary]:fill-primary"
                  }
                />
              </Link>
            </div>
          </div>
          <div className='absolute left-1/2 -translate-x-1/2 lg:hidden'>
            <Link
              href={isAccessDeniedPage ? "#" : "/"}
              aria-label={`${COMPANY.name} Logo`}
              className={
                isAccessDeniedPage ? "w-auto cursor-not-allowed" : "w-auto"
              }
              onClick={
                isAccessDeniedPage ? (e) => e.preventDefault() : closeAllMenus
              }
            >
              <AFSLogo
                width={80}
                height={33}
                className={
                  isAccessDeniedPage
                    ? "[&_path.fill-foreground]:fill-white [&_path.fill-primary]:fill-primary opacity-50"
                    : "[&_path.fill-foreground]:fill-white [&_path.fill-primary]:fill-primary"
                }
              />
            </Link>
          </div>
          <div className='hidden items-end md:flex lg:items-center gap-8 flex-1'>
            <NavPages
              isAuthenticated={!!authProps.session}
              hidden={!shouldShowNav}
              disabled={isAccessDeniedPage}
            />
            <div className='flex items-center gap-4 ml-auto'>
              <div className='hidden lg:block pt-2'>
                <SearchBar
                  searchAction={searchAction}
                  onExpandChange={handleSearchExpandChange}
                  companyId={userCompanyId}
                  disabled={isAccessDeniedPage}
                />
              </div>
            </div>
            <div className='flex items-center space-x-4'>
              <div
                className={`lg:flex hidden gap-4 items-center ${isSearchExpanded ? "hidden" : ""}`}
              >
                <span className='text-sm'>AUD($)</span>
                <CartButton isMobile={false} disabled={isAccessDeniedPage} />
              </div>
              {authProps.session ? (
                <ul className='flex items-center gap-2'>
                  <li
                    className={`lg:flex hidden items-center justify-center ${isSearchExpanded ? "hidden" : ""}`}
                  >
                    {renderAuthButton()}
                  </li>
                </ul>
              ) : (
                <div
                  className={`lg:flex hidden items-center ${isSearchExpanded ? "hidden" : ""}`}
                >
                  {renderAuthButton()}
                </div>
              )}
            </div>
          </div>
          <div className='lg:hidden'>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild disabled={isAccessDeniedPage}>
                <Button
                  variant='outline'
                  size='icon'
                  className='h-9 w-9 relative flex items-center justify-center'
                  aria-label='Toggle Menu'
                  disabled={isAccessDeniedPage}
                >
                  <span className='w-5 h-5 relative flex items-center justify-center'>
                    <span
                      className={`absolute h-0.5 w-5 bg-current transform transition duration-500 ease-in-out ${
                        isOpen ? "rotate-45" : "-translate-y-1.5"
                      }`}
                    />
                    <span
                      className={`absolute h-0.5 w-5 bg-current transform transition duration-500 ease-in-out ${
                        isOpen ? "opacity-0" : "opacity-100"
                      }`}
                    />
                    <span
                      className={`absolute h-0.5 w-5 bg-current transform transition duration-500 ease-in-out ${
                        isOpen ? "-rotate-45" : "translate-y-1.5"
                      }`}
                    />
                  </span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side='right'
                className='w-full border-muted z-[200] p-0 mt-[65px] mb-[76px] h-[calc(100dvh-4rem-4rem-24px)] pointer-events-auto'
              >
                <SheetTitle className='sr-only'>
                  Mobile Navigation Menu
                </SheetTitle>
                <SheetDescription className='sr-only'>
                  Navigation menu for mobile devices with links to various
                  sections of the website
                </SheetDescription>
                <div className='flex flex-col h-full pointer-events-auto'>
                  <div className='flex-grow overflow-y-auto'>
                    <div className='py-2'>
                      <Accordion type='multiple' className='block'>
                        <AccordionItem value='shop'>
                          <AccordionTrigger className='hover:bg-primary hover:text-primary-foreground px-8 py-2'>
                            <Store className='h-5 w-5' />
                            <span>Shop</span>
                          </AccordionTrigger>
                          <AccordionContent>
                            {Object.values(brands).map((brand) => (
                              <Accordion
                                key={brand.label}
                                type='single'
                                collapsible
                                className='block text-md'
                              >
                                <AccordionItem value={brand.label}>
                                  <AccordionTrigger className='hover:bg-primary hover:text-primary-foreground px-8 py-2'>
                                    <ShoppingBag className='h-4 w-4' />
                                    <span>{brand.label}</span>
                                  </AccordionTrigger>
                                  <AccordionContent>
                                    {brand.submenu.map((item) => (
                                      <div key={item.label}>
                                        <Link
                                          href={item.href}
                                          onClick={closeAllMenus}
                                          className='block py-2 pl-12 text-base text-foreground hover:bg-primary hover:text-primary-foreground'
                                        >
                                          {item.label}
                                        </Link>
                                        {"submenu" in item &&
                                          item.submenu?.map((subItem) => (
                                            <Link
                                              key={subItem.label}
                                              href={subItem.href}
                                              onClick={closeAllMenus}
                                              className='block py-2 pl-16 text-sm text-foreground hover:bg-primary hover:text-primary-foreground'
                                            >
                                              {subItem.label}
                                            </Link>
                                          ))}
                                      </div>
                                    ))}
                                  </AccordionContent>
                                </AccordionItem>
                              </Accordion>
                            ))}

                            {authProps.session?.user?.companyName && (
                              <Link
                                href='/company/products'
                                onClick={closeAllMenus}
                                className='block py-2 px-8 text-base text-foreground hover:bg-primary hover:text-primary-foreground'
                              >
                                <div className='flex items-center gap-2'>
                                  <ShoppingBag className='h-4 w-4' />
                                  <span>
                                    {authProps.session.user.companyName}
                                  </span>
                                </div>
                              </Link>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                      <Link
                        href='/contact'
                        onClick={closeAllMenus}
                        className='block px-8 py-2 text-base font-medium text-foreground hover:bg-primary hover:text-primary-foreground'
                      >
                        <div className='flex items-center gap-2'>
                          <Contact className='h-5 w-5' />
                          <span>Contact</span>
                        </div>
                      </Link>
                      <Link
                        href='/distributors'
                        onClick={closeAllMenus}
                        className='block px-8 py-2 text-base font-medium text-foreground hover:bg-primary hover:text-primary-foreground'
                      >
                        <div className='flex items-center gap-2'>
                          <MapPin className='h-5 w-5' />
                          <span>Find a Distributor</span>
                        </div>
                      </Link>

                      {authProps.session && (
                        <>
                          <div className='border-t border-muted my-2'></div>
                          <Link
                            href='/orders'
                            onClick={closeAllMenus}
                            className='block px-8 py-2 text-base font-medium text-foreground hover:bg-primary hover:text-primary-foreground'
                          >
                            <div className='flex items-center gap-2'>
                              <ShoppingCart className='h-5 w-5' />
                              <span>My Orders</span>
                            </div>
                          </Link>
                          <Link
                            href='/quotes'
                            onClick={closeAllMenus}
                            className='block px-8 py-2 text-base font-medium text-foreground hover:bg-primary hover:text-primary-foreground'
                          >
                            <div className='flex items-center gap-2'>
                              <FileText className='h-5 w-5' />
                              <span>My Quotes</span>
                            </div>
                          </Link>
                          <Link
                            href='/my-fleethub'
                            onClick={closeAllMenus}
                            className='block px-8 py-2 text-base font-medium text-foreground hover:bg-primary hover:text-primary-foreground'
                          >
                            <div className='flex items-center gap-2'>
                              <Car className='h-5 w-5' />
                              <span>My FleetHub</span>
                            </div>
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </Container>
    </header>
  );
}
